import type { AiOperationEnvelope, AiOperationalRequest } from '../../../types/aiOperational'
import type { AiOperationalContextWindow } from '../context/operationalRetrieval'

export interface PromptFragmentInput {
  request: AiOperationalRequest
  context: AiOperationalContextWindow
  envelope?: AiOperationEnvelope
}

export const renderLineIdentityFragment = () => [
  'You are Line, the operational AI for Timelining.',
  'You reason about workspace operations, but you never directly write persistent data.',
  'High-impact actions must become reviewable proposals or approved execution plans.',
].join('\n')

export const renderGovernanceFragment = () => [
  'Governance rules:',
  '- Respect workspace permissions and board/item visibility.',
  '- Persistent timeline/checklist mutations must use existing review/proposal/DeltaProcessor paths.',
  '- Economy, admin, integration, destructive, and automation activation actions are never autonomous.',
  '- Explain user-visible consequences without revealing private chain-of-thought.',
].join('\n')

export const renderContextFragment = (context: AiOperationalContextWindow) => [
  'Retrieved operational context:',
  context.digest.summary || 'No relevant records were retrieved.',
  context.digest.omittedReason ? `Context limit: ${context.digest.omittedReason}` : '',
].filter(Boolean).join('\n')

export const renderRetrievalDiagnosticsFragment = (context: AiOperationalContextWindow) => {
  const diagnostics = context.digest.retrievalDiagnostics as {
    sourceCounts?: Record<string, number>
    compression?: { rawCharacterCount?: number; compressedCharacterCount?: number; sliceCount?: number }
    cache?: { hit?: boolean }
    topRanking?: Array<{ id: string; scope: string; score: number }>
  } | undefined
  if (!diagnostics) return ''
  return [
    'Retrieval diagnostics:',
    `Sources: ${Object.entries(diagnostics.sourceCounts ?? {}).map(([scope, count]) => `${scope}=${count}`).join(', ') || 'none'}.`,
    `Compression: ${diagnostics.compression?.rawCharacterCount ?? 0} -> ${diagnostics.compression?.compressedCharacterCount ?? 0} chars across ${diagnostics.compression?.sliceCount ?? 0} slices.`,
    `Cache: ${diagnostics.cache?.hit ? 'hit' : 'miss'}.`,
    diagnostics.topRanking?.length
      ? `Top ranking: ${diagnostics.topRanking.slice(0, 3).map((entry) => `${entry.scope}:${entry.id}:${entry.score}`).join(', ')}.`
      : '',
  ].filter(Boolean).join('\n')
}

export const renderWorkspaceMemoryFragment = (context: AiOperationalContextWindow) => {
  const insights = context.digest.memoryInsights ?? []
  if (!insights.length) {
    return [
      'Workspace operational memory:',
      'No durable operational pattern was inferred from the bounded context window.',
    ].join('\n')
  }
  return [
    'Workspace operational memory:',
    ...insights.map((insight) =>
      `- ${insight.kind}: ${insight.summary} (confidence=${insight.confidence}, retention=${insight.retentionDays}d, source=${insight.source})`),
  ].join('\n')
}

export const renderOperationalForecastFragment = (context: AiOperationalContextWindow) => {
  const forecasts = context.digest.forecasts ?? []
  if (!forecasts.length) {
    return [
      'Operational forecast:',
      'No immediate drift, readiness, missing-step, or workload concentration signal was detected from the bounded context.',
    ].join('\n')
  }
  return [
    'Operational forecast:',
    ...forecasts.map((forecast) =>
      `- ${forecast.severity} ${forecast.kind}: ${forecast.summary} Recommended action: ${forecast.recommendedAction}`),
  ].join('\n')
}

export const renderOperationalContinuityFragment = (context: AiOperationalContextWindow) => {
  const recommendations = context.digest.recommendations ?? []
  const signals = context.digest.collaborationSignals ?? []
  const threads = context.digest.continuityThreads ?? []
  return [
    'Operational continuity:',
    recommendations.length
      ? `Next steps: ${recommendations.map((item) => `${item.priority} ${item.kind}: ${item.suggestedActionLabel}`).join(' | ')}`
      : 'No immediate operational next step was derived from the bounded context.',
    signals.length
      ? `Collaboration signals: ${signals.map((signal) => `${signal.severity} ${signal.kind}: ${signal.summary}`).join(' | ')}`
      : 'No proposal collision, stale-assumption, or dependency conflict signal was detected.',
    threads.length
      ? `Connected systems: ${threads.flatMap((thread) => thread.relatedSystems).filter((value, index, all) => all.indexOf(value) === index).join(', ')}`
      : '',
  ].filter(Boolean).join('\n')
}

export const renderOperationContractFragment = () => [
  'Operation planning contract:',
  '- Return structured operations with target records, risk level, review requirement, and rollback metadata.',
  '- Low-risk UI guidance/summaries may be immediate.',
  '- Medium-risk operations require confirmation.',
  '- High-risk data changes require proposal review.',
  '- Restricted operations should be refused or converted into safe planning work.',
].join('\n')

export const renderOperationalIntelligenceFragment = (envelope?: AiOperationEnvelope) => {
  const preview = envelope?.simulation?.intelligencePreview
  if (!preview) return ''
  return [
    'Operational intelligence preview:',
    `Domain: ${preview.domain}`,
    `Summary: ${preview.summary}`,
    `Review path: ${preview.reviewPath}`,
    `Constraints: ${preview.constraints.join(' ')}`,
  ].join('\n')
}

export const renderExecutionPlanFragment = (envelope?: AiOperationEnvelope) => {
  const executionPlan = envelope?.executionPlan
  if (!executionPlan) return ''
  const executionGraph = envelope?.executionGraph
  return [
    'Execution plan:',
    `Summary: ${executionPlan.summary}`,
    executionGraph ? `Graph state: ${executionGraph.state}` : '',
    executionGraph ? `Graph size: ${executionGraph.steps.length} steps, ${executionGraph.dependencies.length} dependencies` : '',
    `Auto-start: ${executionPlan.canAutoStart ? 'yes' : 'no'}`,
    `Steps: ${executionPlan.steps.map((step) => `${step.kind}/${step.status}`).join(', ')}`,
    `Checkpoints: ${(executionGraph?.checkpoints ?? executionPlan.checkpoints).map((checkpoint) => `${checkpoint.satisfiedBy}/${checkpoint.state ?? 'PENDING'}`).join(', ') || 'none'}`,
  ].filter(Boolean).join('\n')
}

export const renderOperationalPrompt = (input: PromptFragmentInput) => [
  renderLineIdentityFragment(),
  renderGovernanceFragment(),
  `Surface: ${input.request.surface}`,
  `Mode: ${input.request.mode}`,
  `User request: ${input.request.prompt}`,
  renderContextFragment(input.context),
  renderRetrievalDiagnosticsFragment(input.context),
  renderWorkspaceMemoryFragment(input.context),
  renderOperationalForecastFragment(input.context),
  renderOperationalContinuityFragment(input.context),
  renderOperationalIntelligenceFragment(input.envelope),
  renderExecutionPlanFragment(input.envelope),
  renderOperationContractFragment(),
  input.envelope ? `Draft operation envelope:\n${JSON.stringify(input.envelope, null, 2)}` : '',
].filter(Boolean).join('\n\n')
